import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { GraduationCap, Users, BookOpen, Target, TrendingUp } from 'lucide-react';
import ConsultationModal from '../components/ConsultationModal';

const WHATSAPP_URL = 'https://wa.me/916366979916?text=Hi%20CloudXAI%2C%20I%20am%20interested%20in%20Corporate%20Training%20services';

const CorporateTrainingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0B0F1A]">
      <Header />
      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} serviceType="Corporate Training" />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden" style={{
        background: 'linear-gradient(135deg, #0B0F1A 0%, #020617 100%)'
      }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'linear-gradient(#00E5FF 1px, transparent 1px), linear-gradient(90deg, #00E5FF 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <Link to="/" className="inline-flex items-center text-[#00E5FF] hover:text-[#1DE9B6] mb-8 transition-colors">
            ← Back to Home
          </Link>
          
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center shadow-xl shadow-[#00E5FF]/50">
              <GraduationCap className="w-8 h-8 text-[#0B0F1A]" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-white font-['Space_Grotesk']">
              Corporate Training & Placement
            </h1>
          </div>
          <div className="w-32 h-1 bg-gradient-to-r from-[#00E5FF] to-transparent mb-6"></div>
          <p className="text-xl text-[#9CA3AF] max-w-4xl">
            Industry-aligned programs for students, professionals & enterprises
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#111827]">
        <div className="max-w-7xl mx-auto space-y-16">
          
          {/* Corporate IT Training */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Corporate IT Training</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">
                  We deliver customized corporate IT training programs designed to align with business goals, technology roadmaps, and team skill gaps. Our training is practical, instructor-led, and focused on real-world implementation.
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Key Areas:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Cloud Computing (AWS, Azure, GCP)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">DevOps & Site Reliability Engineering</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Data Engineering & AI</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Cyber Security</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Full Stack Development</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Training Modes:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Online | Onsite | Hybrid</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Customized curriculum</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Hands-on labs & case studies</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Professional Training Programs */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Users className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Professional Training Programs</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">
                  Our professional training programs are built for working professionals and career switchers seeking upskilling or role transitions.
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Who This Is For:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Working professionals upgrading skills</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Career transition candidates</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Certification-focused learners</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Program Highlights:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Industry-aligned curriculum</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Real-time project exposure</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Flexible schedules</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                    <span className="text-[#9CA3AF]">Career mentoring</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Student & Fresher Training */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Student & Fresher Training</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">
                  We prepare students and fresh graduates to become job-ready professionals through structured learning and hands-on experience.
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">What We Offer:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Foundation to advanced skill training</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Internship & live project exposure</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Final-year project guidance</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Industry-readiness programs</span>
                </div>
              </div>
            </div>
          </div>

          {/* Placement Assistance */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <Target className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Placement Assistance</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">
                  Our placement assistance program focuses on end-to-end career readiness, not just job referrals.
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Placement Support Includes:</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Resume & LinkedIn profile optimization</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Mock interviews & interview coaching</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Placement drives & recruiter connects</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Continuous career guidance</span>
                </div>
              </div>
            </div>
          </div>

          {/* Corporate Workshops */}
          <div className="bg-[#0B0F1A] border-2 border-[#00E5FF]/20 rounded-2xl p-8 hover:border-[#00E5FF]/50 transition-all duration-300">
            <div className="flex items-start space-x-6 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-7 h-7 text-[#0B0F1A]" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4 font-['Space_Grotesk']">Corporate Workshops</h2>
                <p className="text-[#9CA3AF] text-lg mb-6">
                  We conduct focused workshops to help organizations and teams stay updated with emerging technologies.
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-[#00E5FF] font-semibold mb-4 text-lg">Workshop Topics:</h4>
              <div className="space-y-2">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Cloud & AI awareness sessions</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Technology leadership programs</span>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 rounded-full bg-[#00E5FF] mt-2"></div>
                  <span className="text-[#9CA3AF]">Digital transformation readiness</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Featured Courses Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 font-['Space_Grotesk']">
              Our <span className="text-[#00E5FF]">Training Programs</span>
            </h2>
            <p className="text-lg text-[#9CA3AF] max-w-2xl mx-auto">
              Industry-aligned courses designed to make you job-ready with hands-on experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* AI & ML Course */}
            <div className="bg-[#111827] border-2 border-[#a78bfa]/30 rounded-2xl p-6 hover:border-[#a78bfa]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#a78bfa] to-[#7c3aed] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#a78bfa]/20 text-[#a78bfa] text-sm font-semibold rounded-full">10 Weeks</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#a78bfa] transition-colors">AI & Machine Learning</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">Master Python, TensorFlow, Neural Networks, and deploy real-world AI solutions</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Python</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">TensorFlow</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Deep Learning</span>
              </div>
            </div>

            {/* Azure Data Engineer + GenAI */}
            <div className="bg-[#111827] border-2 border-[#0078D4]/30 rounded-2xl p-6 hover:border-[#0078D4]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0078D4] to-[#00BCF2] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#0078D4]/20 text-[#00BCF2] text-sm font-semibold rounded-full">3 Months</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#00BCF2] transition-colors">Azure Data Engineer + GenAI</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">Complete Azure DP-203 certification with Generative AI integration and real projects</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Azure</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Data Factory</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">GenAI</span>
              </div>
            </div>

            {/* Cyber Security */}
            <div className="bg-[#111827] border-2 border-[#f472b6]/30 rounded-2xl p-6 hover:border-[#f472b6]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f472b6] to-[#ec4899] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#f472b6]/20 text-[#f472b6] text-sm font-semibold rounded-full">2 Months</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#f472b6] transition-colors">Cyber Security</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">Ethical hacking, penetration testing, SOC analyst training with industry certifications</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Ethical Hacking</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">SIEM</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">SOC</span>
              </div>
            </div>

            {/* AWS DevOps */}
            <div className="bg-[#111827] border-2 border-[#FF9900]/30 rounded-2xl p-6 hover:border-[#FF9900]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF9900] to-[#FF6600] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#FF9900]/20 text-[#FF9900] text-sm font-semibold rounded-full">3 Months</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#FF9900] transition-colors">AWS DevOps</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">CI/CD pipelines, Infrastructure as Code, containerization with AWS certification prep</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">AWS</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Docker</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Kubernetes</span>
              </div>
            </div>

            {/* Python & SQL Foundation */}
            <div className="bg-[#111827] border-2 border-[#00E5FF]/30 rounded-2xl p-6 hover:border-[#00E5FF]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#1DE9B6] flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#0B0F1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#00E5FF]/20 text-[#00E5FF] text-sm font-semibold rounded-full">2 Months</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#00E5FF] transition-colors">Python & SQL Foundation</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">Essential programming skills with Python and database management with SQL</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Python</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">SQL</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Data Analysis</span>
              </div>
            </div>

            {/* Snowflake Data Engineering */}
            <div className="bg-[#111827] border-2 border-[#29B5E8]/30 rounded-2xl p-6 hover:border-[#29B5E8]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#29B5E8] to-[#0B6CB0] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#29B5E8]/20 text-[#29B5E8] text-sm font-semibold rounded-full">2 Months</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#29B5E8] transition-colors">Snowflake Data Engineering</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">Cloud data warehousing, ETL pipelines, and Snowflake certification preparation</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Snowflake</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">ETL</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Data Warehouse</span>
              </div>
            </div>

            {/* AI Testing & Automation */}
            <div className="bg-[#111827] border-2 border-[#10b981]/30 rounded-2xl p-6 hover:border-[#10b981]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#10b981] to-[#059669] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full">9 Weeks</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#10b981] transition-colors">AI Testing & Automation</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">AI-powered testing frameworks, test automation with ML, and intelligent QA strategies</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">AI Testing</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Selenium</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Automation</span>
              </div>
            </div>

            {/* Microsoft Fabric Data Engineer */}
            <div className="bg-[#111827] border-2 border-[#742774]/30 rounded-2xl p-6 hover:border-[#742774]/60 transition-all duration-300 group">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#742774] to-[#b044b0] flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <span className="px-3 py-1 bg-[#742774]/20 text-[#b044b0] text-sm font-semibold rounded-full">12 Weeks</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#b044b0] transition-colors">Microsoft Fabric Data Engineer</h3>
              <p className="text-[#9CA3AF] text-sm mb-4">End-to-end data engineering with Microsoft Fabric, Lakehouse, Dataflows, and real-time analytics</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Microsoft Fabric</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Lakehouse</span>
                <span className="px-2 py-1 bg-[#0B0F1A] text-[#9CA3AF] text-xs rounded">Power BI</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-10">
            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-4 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300"
            >
              Enquire About Courses
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0F1A]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
            Ready to Transform Your Team?
          </h2>
          <p className="text-xl text-[#9CA3AF] mb-8">
            Let's discuss how our training programs can help achieve your business goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold px-8 py-6 text-lg rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300"
            >
              Request Consultation
            </Button>
            <Button
              onClick={() => window.open(WHATSAPP_URL, '_blank')}
              className="border-2 border-[#00E5FF] text-[#00E5FF] hover:bg-[#00E5FF]/10 px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-transparent"
            >
              Talk to Our Experts
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CorporateTrainingPage;
